import { authErrorMessage, t } from '@glory/i18n';
import { MIN_PASSWORD_LENGTH } from '@glory/identity/domain';
import { AuthLayout, Button, Callout, notify, PasswordField } from '@glory/ui';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { type SubmitEvent, useState } from 'react';

import { passwordProblem } from '../features/auth/password.ts';
import { authClient, authErrorCode } from '../lib/auth-client.ts';

interface ResetSearch {
  readonly token?: string | undefined;
}

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>): ResetSearch => ({
    token: typeof search.token === 'string' && search.token !== '' ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { token } = Route.useSearch();
  const router = useRouter();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ password?: string; repeat?: string }>({});
  const [error, setError] = useState<{ message: string; expired: boolean } | null>(null);
  const [pending, setPending] = useState(false);

  if (!token) {
    return (
      <AuthLayout title={t('auth.resetTitle')}>
        <Callout>{t('auth.resetNoToken')}</Callout>
        <Button asChild variant="primary" size="lg">
          <Link to="/forgot-password">{t('auth.requestNewLink')}</Link>
        </Button>
      </AuthLayout>
    );
  }

  const submit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = {
      password: passwordProblem(password),
      repeat: repeat !== password ? t('auth.passwordsMismatch') : undefined,
    };
    setErrors(next);
    if (next.password ?? next.repeat) return;
    setPending(true);
    setError(null);
    const { error: failure } = await authClient.resetPassword({ newPassword: password, token });
    setPending(false);
    if (failure) {
      setError({ message: authErrorMessage(failure), expired: authErrorCode(failure) === 'INVALID_TOKEN' });
      return;
    }
    notify(t('auth.resetDone'));
    await router.navigate({ to: '/login', replace: true });
  };

  return (
    <AuthLayout title={t('auth.resetTitle')} lead={t('auth.resetLead')}>
      <form className="flex flex-col gap-4" noValidate onSubmit={(e) => void submit(e)}>
        <PasswordField
          label={t('auth.newPassword')}
          description={t('auth.newPasswordHint', { min: MIN_PASSWORD_LENGTH })}
          size="lg"
          autoFocus
          autoComplete="new-password"
          required
          value={password}
          onValueChange={setPassword}
          error={errors.password}
        />
        <PasswordField
          label={t('auth.repeatPassword')}
          size="lg"
          autoComplete="new-password"
          required
          value={repeat}
          onValueChange={setRepeat}
          error={errors.repeat}
        />
        {error ? <Callout>{error.message}</Callout> : null}
        {error?.expired ? (
          <Button asChild size="lg">
            <Link to="/forgot-password">{t('auth.requestNewLink')}</Link>
          </Button>
        ) : (
          <Button type="submit" variant="primary" size="lg" loading={pending}>
            {t('auth.saveNewPassword')}
          </Button>
        )}
      </form>
    </AuthLayout>
  );
}
